import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import { adminApi } from '@/api/tmm/admin';
import type { FleetDashboard, ResellerDto, TenantView } from '@/api/tmm/types';
import { useCopy } from '@/hooks/useCopy';
import { ErrorState } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ApiError } from '@/api/errors';
import { TenantOverviewPanel } from '../tenants/TenantOverviewPanel';
import { OffersPanel } from '../tenants/OffersPanel';
import { PromotionsPanel } from '../tenants/PromotionsPanel';
import { TenantAddonsPanel } from '../tenants/TenantAddonsPanel';
import { SubscriptionsPanel } from '../tenants/SubscriptionsPanel';
import { StatementsPanel } from '../tenants/StatementsPanel';
import { SeatsPanel } from '../tenants/SeatsPanel';
import { AiUsagePanel } from '../tenants/AiUsagePanel';
import { TenantRevenuePanel } from '../tenants/TenantRevenuePanel';
import { DeletionRequestsPanel } from '../tenants/DeletionRequestsPanel';
import { TenantAffiliatesPanel } from '../affiliates/TenantAffiliatesPanel';
import { PayoutStatementsPanel } from '../tenants/PayoutStatementsPanel';
import { MembersPanel } from '../tenants/MembersPanel';
import { CustomersPanel } from '../tenants/CustomersPanel';
import { InvitationsPanel } from '../tenants/InvitationsPanel';
import { WhitelabelDomainPanel } from '../tenants/WhitelabelDomainPanel';
import { PaymentsSetupPanel } from '../tenants/PaymentsSetupPanel';
import { DORMANT_TENANT_ADMINISTRATORS, type TabKey } from './partnerSections';
import { PeopleLeafLayout } from './peopleLeafLayout';

export function usePartnerTenant(slug: string | null, owner?: ResellerDto) {
  const generation = useRef(0);
  const [token, setToken] = useState(0);
  // A newer owner response invalidates an older full-tenant snapshot immediately, before effects.
  const key = useMemo(() => ({ slug, token, owner }), [slug, token, owner]);
  const activeKey = useRef(key);
  useLayoutEffect(() => {
    activeKey.current = key;
  }, [key]);
  const [response, setResponse] = useState<{
    key: typeof key;
    tenant: TenantView | null;
    failure: unknown;
  } | null>(null);
  useEffect(() => {
    if (slug === null) return;
    let cancelled = false;
    const requestGeneration = ++generation.current;
    adminApi
      .getTenant(slug)
      .then((tenant) => {
        if (!cancelled && generation.current === requestGeneration)
          setResponse({ key, tenant, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled && generation.current === requestGeneration)
          setResponse({ key, tenant: null, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, key]);
  return {
    tenant: response?.key === key ? response.tenant : null,
    failure: response?.key === key ? response.failure : null,
    loading: slug !== null && response?.key !== key,
    reload: () => setToken((n) => n + 1),
    update: (tenant: TenantView) => {
      if (activeKey.current !== key) return;
      generation.current++;
      setResponse({ key, tenant, failure: null });
    },
  };
}

/**
 * "No tenant yet" with Create tenant… when the server says one can be created, else why not
 * (`tenantCreatableReason`). TMMPDM-0088: exported so the Overview's Needs you shows it as item 5.
 */
export function NoTenantNotice({
  reseller,
  onCreate,
}: {
  reseller: ResellerDto;
  onCreate: () => void;
}) {
  const { t } = useCopy();
  return (
    <Alert
      severity="info"
      action={
        reseller.tenantCreatable ? (
          <Button color="inherit" onClick={onCreate}>
            {t('partners.createTenant', 'Create tenant…')}
          </Button>
        ) : undefined
      }
    >
      {reseller.tenantCreatable
        ? t('partners.noTenant', 'No tenant yet')
        : reseller.tenantCreatableReason === 'RESELLER_SUSPENDED'
          ? t(
              'partners.createTenantSuspended',
              'This partner is suspended. Reactivate it before creating its tenant.',
            )
          : reseller.tenantCreatableReason === 'RESELLER_DEACTIVATED'
            ? t('partners.createTenantDeactivated', 'This partner is deactivated.')
            : t('partners.createTenantHasTenant', 'This partner already has a tenant.')}
    </Alert>
  );
}

export function PartnerTenantPanel({
  tab,
  reseller,
  load,
  onCreate,
  tenantDashboard = adminApi.tenantDashboard,
  tenantEditOpen = false,
  onTenantEditOpenChange,
  noTenantNotice = true,
  showSellable = true,
}: {
  tab: TabKey | typeof DORMANT_TENANT_ADMINISTRATORS;
  reseller: ResellerDto;
  load: ReturnType<typeof usePartnerTenant>;
  onCreate: () => void;
  tenantDashboard?: (slug: string, query: { from: string; to: string }) => Promise<FleetDashboard>;
  tenantEditOpen?: boolean;
  onTenantEditOpenChange?: (open: boolean) => void;
  /** TMMPDM-0088: the Overview shows the notice in Needs you, so its tenant panel renders nothing. */
  noTenantNotice?: boolean;
  /** TMMPDM-0088: the Overview shows Can't sell in Needs you, so its tenant overview skips it. */
  showSellable?: boolean;
}) {
  const { t } = useCopy();
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);
  if (reseller.tenant === null)
    return noTenantNotice ? <NoTenantNotice reseller={reseller} onCreate={onCreate} /> : null;
  if (load.loading) return <LoadingSpinner />;
  if (load.failure || !load.tenant)
    return (
      <ErrorState
        error={load.failure}
        message={
          load.failure instanceof ApiError && load.failure.status === 404
            ? t('tenantDetail.notFound', 'Tenant not found.')
            : t('tenantDetail.identity.loadError', 'Could not load tenant identity.')
        }
        onRetry={load.reload}
      />
    );
  const row = load.tenant;
  switch (tab) {
    case 'overview':
      return (
        <TenantOverviewPanel
          tenant={row}
          onResellerChanged={load.reload}
          onUpdated={load.update}
          tenantDashboard={tenantDashboard}
          showDetails={false}
          showSellable={showSellable}
          {...(onTenantEditOpenChange
            ? { editOpen: tenantEditOpen, onEditOpenChange: onTenantEditOpenChange }
            : {})}
        />
      );
    case 'offers':
      return <OffersPanel slug={row.slug} resellerId={row.reseller?.id ?? null} />;
    case 'promotions':
      return <PromotionsPanel slug={row.slug} />;
    case 'addons':
      return <TenantAddonsPanel slug={row.slug} />;
    case 'subscriptions':
      return <SubscriptionsPanel tenant={row} />;
    case 'statements':
      return <StatementsPanel slug={row.slug} currency={row.currency} />;
    case 'seats':
      return <SeatsPanel slug={row.slug} currency={row.currency} />;
    case 'aiUsage':
      return <AiUsagePanel slug={row.slug} />;
    case 'revenue':
      return <TenantRevenuePanel slug={row.slug} currency={row.currency} />;
    case 'deletions':
      return <DeletionRequestsPanel slug={row.slug} />;
    case 'affiliates':
      return <TenantAffiliatesPanel slug={row.slug} resellerId={reseller.id} />;
    case 'affiliatePayouts':
      return (
        <PayoutStatementsPanel
          slug={row.slug}
          resellerId={row.reseller?.id ?? null}
          resellerName={row.reseller?.name ?? null}
        />
      );
    // Dormant (TMMPDM-0085): hidden from People; not used in the current model.
    case DORMANT_TENANT_ADMINISTRATORS:
      return (
        <PeopleLeafLayout
          caption={
            <Typography>
              {t(
                'partners.tenantAdministrators.caption',
                "Sign in to the tenant's app to run the business.",
              )}
            </Typography>
          }
          invitations={
            <InvitationsPanel
              tenantId={row.id}
              refreshKey={invitationsRefreshKey}
              audience="staff"
            />
          }
        >
          <MembersPanel
            tenantId={row.id}
            onMemberInvited={() => setInvitationsRefreshKey((n) => n + 1)}
          />
        </PeopleLeafLayout>
      );
    case 'customers':
      return <CustomersPanel tenantId={row.id} slug={row.slug} />;
    case 'domain':
      return <WhitelabelDomainPanel slug={row.slug} />;
    case 'payments':
      return <PaymentsSetupPanel key={row.slug} tenant={row} onUpdated={load.update} />;
    default:
      return null;
  }
}
